import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { useParams } from "react-router-dom";
import { Crumbs } from "../../components/crumb";
import { Button } from "../../components/button";
import {
  InformationCircleIcon,
  MapPinIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { Checkpoints } from "./checkpoints/index";
import { CheckpointDetails } from "./checkpoints/details";
import { SecondaryTabs } from "../../components/tabs/secondary";
import { UnderlineTabs } from "../../components/tabs/underline";
import { Tours } from "./tours";
import { Posts } from "../posts";
import { Files } from "../files";
import { TourDetails } from "./tours/details";
import { SiteDetailsMeta } from "./siteDetailsMeta";
import { AuthContext } from "../../contexts/AuthContext";

function SiteSettingsDetails() {
  const params = useParams();
  const { authContext } = useContext(AuthContext);
  const authFetch = useAuthFetch();
  const [isCheckpointsLoading, setIsCheckpointsLoading] = useState(false);
  const [isToursLoading, setIsToursLoading] = useState(false);

  const [allowedCheckpoints, setAllowedCheckpoints] = useState(0);
  const [checkpointCount, setCheckpointCount] = useState(0);

  useEffect(() => {
    if (authContext.organization.planFeatures.Checkpoints) {
      setAllowedCheckpoints(authContext.organization.planFeatures.Checkpoints);
    }
  }, [authContext.organization]);

  /// Get checkpoints ///
  const [checkpointsListData, setCheckpointsListData] = useState();

  const fetchCheckpointsList = useCallback(async () => {
    setIsCheckpointsLoading(true);
    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/sites/${params.id}/checkpoints`,
      );
      if (response.ok) {
        const data = await response.json();
        setCheckpointsListData(data);
        setCheckpointCount(data.length);
      } else {
        console.error("Error fetching checkpoints list");
      }
    } catch (error) {
      console.error("Error fetching checkpoints list", error);
    } finally {
      await timerPromise;
      setIsCheckpointsLoading(false);
    }
  });

  /// Checkpoints ///
  const [isCheckpointsAddMode, setIsCheckpointsAddMode] = useState(false);
  const [selectedCheckpointType, setSelectedCheckpointType] = useState(null);

  const handleCheckpointsAddMode = (id, type) => {
    setSelectedCheckpointId(id);
    setSelectedCheckpointType(type);
    setIsCheckpointsAddMode(false);
    toggleCheckpointsOpen();
  };

  const [isCheckpointsOpen, setIsCheckpointsOpen] = useState(false);
  const toggleCheckpointsOpen = () => {
    setIsCheckpointsOpen(!isCheckpointsOpen);
    if (isCheckpointsOpen) {
      setSelectedCheckpointId(null);
    }
  };

  const [selectedCheckpointId, setSelectedCheckpointId] = useState(null);
  const handleSelectedCheckpointsId = (id) => {
    setSelectedCheckpointId(id);
  };

  /// Selected checkpoint details ///
  const [selectedCheckpointDetails, setSelectedCheckpointDetails] = useState({
    checkpoint_name: "",
    checkpoint_description: "",
    checkpoint_type: "",
  });

  const fetchSelectedCheckpointDetails = useCallback(async () => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/sites/checkpoints/${selectedCheckpointId}`,
      );
      if (response.ok) {
        const data = await response.json();
        const { checkpoint_name, checkpoint_description, checkpoint_type } =
          data[0];

        setSelectedCheckpointDetails({
          checkpoint_name,
          checkpoint_description,
          checkpoint_type,
        });
      } else {
        console.error("Error fetching selected checkpoint details");
      }
    } catch (error) {
      console.error("Error fetching selected checkpoint details", error);
    }
  }, [selectedCheckpointId, setSelectedCheckpointDetails]);

  /// Tours ///
  const [tours, setTours] = useState([]);
  const [isToursAddMode, setIsToursAddMode] = useState(false);

  const handleToursAddMode = (id) => {
    setSelectedTourId(id);
    setIsToursAddMode(false);
    toggleToursOpen();
  };

  const [isToursOpen, setIsToursOpen] = useState(false);
  const toggleToursOpen = () => {
    setIsToursOpen(!isToursOpen);
    if (isToursOpen) {
      setSelectedTourId(null);
    }
  };

  const [selectedTourId, setSelectedTourId] = useState(null);
  const handleSelectedTourId = (id) => {
    setSelectedTourId(id);
  };

  /// Tour list data ///
  const [toursListData, setToursListData] = useState();

  const fetchToursList = useCallback(async () => {
    setIsToursLoading(true);
    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/sites/${params.id}/tours`,
      );
      if (response.ok) {
        const data = await response.json();
        setToursListData(data);
      } else {
        console.error("Error fetching tours list");
      }
    } catch (error) {
      console.error("Error fetching tours list", error);
    } finally {
      await timerPromise;
      setIsToursLoading(false);
    }
  });

  /// Selected tour details ///
  const [selectedTourDetails, setSelectedTourDetails] = useState({
    tour_name: "",
    tour_description: "",
  });

  const fetchSelectedTourDetails = useCallback(async () => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/sites/tours/${selectedTourId}`,
      );
      if (response.ok) {
        const data = await response.json();
        const { tour_name, tour_description } = data;

        setSelectedTourDetails({ tour_name, tour_description });
      } else {
        console.error("Error fetching selected tour details");
      }
    } catch (error) {
      console.error("Error fetching selected tour details", error);
    }
  }, [selectedTourId, setSelectedTourDetails]);

  /// Tour checkpoints ///
  const [selectedTourCheckpoints, setSelectedTourCheckpoints] = useState([]);
  const handleSelectedTourCheckpoints = (checkpoints) => {
    setSelectedTourCheckpoints(checkpoints);
  };

  /// Delete checkpoints from both checkpoints list and tour details list ///
  const [isCheckpointsDeleteOpen, setIsCheckpointsDeleteOpen] = useState(false);
  const [selectedCheckpointDeleteItem, setSelectedCheckpointDeleteItem] =
    useState({
      identifier: "",
      id: "",
    });

  const [deleteCheckpointSuccess, setDeleteCheckpointSuccess] = useState({
    displaying: false,
    message: "Checkpoint has been deleted",
  });

  const handleDeleteCheckpointSuccess = () => {
    fetchCheckpointsList();

    setDeleteCheckpointSuccess({
      displaying: true,
      message: "Checkpoint has been deleted",
    });
    setTimeout(() => {
      setDeleteCheckpointSuccess({
        displaying: false,
        message: deleteCheckpointSuccess.message,
      });
    }, 3000);
  };

  const deleteCheckpoint = (checkpointId) => {
    return authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/checkpoints/delete/${checkpointId}`,
      {
        method: "POST",
      },
    )
      .then((response) => response.json())
      .then((result) => {
        // Handle the result
        return result; // Ensure you return something here if needed
      })
      .catch((error) => {
        console.error("Error:", error);
        // Handle any errors that occur during submission
        throw error; // Re-throw the error if you want to propagate it
      });
  };

  function handleDeleteCheckpoint(identifier, id) {
    setSelectedCheckpointDeleteItem({ identifier, id }); // Correctly using the setter function
    setIsCheckpointsDeleteOpen(true);
  }

  const toggleDeleteCheckpointState = (setState, state) => {
    setState(!state);
  };

  /// Delete checkpoints from both checkpoints list and tour details list ///
  const [isToursDeleteOpen, setIsToursDeleteOpen] = useState(false);
  const [selectedTourDeleteItem, setSelectedTourDeleteItem] = useState({
    identifier: "",
    id: "",
  });

  const [deleteTourSuccess, setDeleteTourSuccess] = useState({
    displaying: false,
    message: "Checkpoint has been deleted",
  });

  const handleDeleteTourSuccess = () => {
    fetchToursList();

    setDeleteTourSuccess({
      displaying: true,
      message: "Tour has been deleted",
    });
    setTimeout(() => {
      setDeleteTourSuccess({
        displaying: false,
        message: deleteTourSuccess.message,
      });
    }, 3000);
  };

  const deleteTour = (tourId) => {
    return authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/tours/delete/${tourId}`,
      {
        method: "POST",
      },
    )
      .then((response) => response.json())
      .then((result) => {
        // Handle the result
        return result; // Ensure you return something here if needed
      })
      .catch((error) => {
        console.error("Error:", error);
        // Handle any errors that occur during submission
        throw error; // Re-throw the error if you want to propagate it
      });
  };

  function handleDeleteTour(identifier, id) {
    setSelectedTourDeleteItem({ identifier, id }); // Correctly using the setter function
    setIsToursDeleteOpen(true);
  }

  const toggleDeleteTourState = (setState, state) => {
    setState(!state);
  };

  /// Details Meta ///
  const [isDetailsMetaOpen, setIsDetailsMetaOpen] = useState(false);
  const toggleDetailsMetaOpen = () => {
    setIsDetailsMetaOpen(!isDetailsMetaOpen);
  };

  /// Fetch site details ///
  const [backendData, setBackendData] = useState({
    site_name: "",
    site_address: {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
    site_notes: "",
    customer_id: "",
    customer_name: "",
    logo_url: "",
    contacts: [],
  });

  const fetchSiteDetails = async () => {
    const fetchPromise = authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/${params.id}`,
    )
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          const {
            site_name,
            street,
            city,
            state,
            zip,
            country,
            site_notes,
            customer_id,
            customer_name,
            logo_url,
            contacts,
            geofence_latitude,
            geofence_longitude,
            geofence_radius_m,
          } = data[0];
          setBackendData({
            site_name,
            site_address: {
              street,
              city,
              state,
              zip,
              country,
            },
            site_notes,
            customer_id,
            customer_name,
            logo_url,
            contacts,
            geofence:
              geofence_radius_m != null
                ? {
                    latitude:
                      geofence_latitude != null
                        ? Number(geofence_latitude)
                        : null,
                    longitude:
                      geofence_longitude != null
                        ? Number(geofence_longitude)
                        : null,
                    radius_m: Number(geofence_radius_m),
                  }
                : { latitude: null, longitude: null, radius_m: null },
          });
        } else {
          // Handle HTTP errors e.g., 404, 500 etc.
          console.error("HTTP Error: ", response.statusText);
          // Optionally, update state to display an error message
        }
      })
      .catch((error) => {
        // Handle network errors, parsing errors, etc.
        console.error("Fetching error: ", error.message);
        // Optionally, update state to display an error message
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      // setInitialLoadCompleted(true); // Set initial load completed after the first fetch
    });
  };

  const handleSuccess = () => {
    fetchSiteDetails();
  };

  useEffect(() => {
    fetchSiteDetails();
  }, [params.id]);

  /// Posts ///
  // null = sheet closed, {} = creating, a post = editing it.
  const [editingPost, setEditingPost] = useState(null);

  /// Files ///
  const filesRef = useRef(null);

  /// Tabs ///

  const secondaryTabs = [
    {
      name: "Posts",
      href: "#",
      current: true,
      component: (
        <Posts
          siteId={params.id}
          editing={editingPost}
          setEditing={setEditingPost}
        />
      ),
    },
    {
      name: "Tours",
      href: "#",
      current: false,
      component: (
        <Tours
          handleToursAddMode={handleToursAddMode}
          fetchToursList={fetchToursList}
          isLoading={isToursLoading}
          toursListData={toursListData}
          setIsCheckpointsAddMode={setIsCheckpointsAddMode}
          handleDeleteTour={handleDeleteTour}
        />
      ),
    },
    {
      name: "Checkpoints",
      href: "#",
      current: false,
      component: (
        <Checkpoints
          checkpoints={checkpointsListData}
          setCheckpoints={setCheckpointsListData}
          handleDeleteCheckpoint={handleDeleteCheckpoint}
          fetchCheckpointsList={fetchCheckpointsList}
          isLoading={isCheckpointsLoading}
          checkpointsListData={checkpointsListData}
          handleCheckpointsAddMode={handleCheckpointsAddMode}
          setIsCheckpointsAddMode={setIsCheckpointsAddMode}
          toggleOpen={toggleCheckpointsOpen}
        />
      ),
    },
    {
      name: "Files",
      href: "#",
      current: false,
      component: (
        <Files siteId={params.id} variant="table" ref={filesRef} />
      ),
    },
  ];

  const [activeTab, setActiveTab] = useState("Posts");

  return (
    <div>
      <Crumbs
        section="Sites"
        previous="/sites/"
        active={backendData.site_name}
      />

      <main className="py-4">
        <div className="sm:px-4 lg:px-6">
          <div
            className="text-sm font-medium py-2 sm:px-4 lg:px-4 mb-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer rounded-md"
            onClick={toggleDetailsMetaOpen}
          >
            <div className="inline-block mr-8">
              <InformationCircleIcon
                className="h-6 w-6 mr-2 -ml-2 inline text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <span className="text-gray-600 dark:text-gray-300">
                {backendData.site_name}
              </span>
            </div>
            <div className="inline-block mr-8">
              <MapPinIcon
                className="h-6 w-6 mr-2 inline text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <span className="text-gray-600 dark:text-gray-300">
                {backendData.site_address.street}
              </span>
            </div>
            <div className="inline-block mr-8">
              <DocumentTextIcon
                className="h-6 w-6 mr-2 inline text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <span className="text-gray-600 dark:text-gray-300">
                {!backendData.site_notes ? "No notes" : "View notes"}
              </span>
            </div>
            <div className="inline-block mr-8">
              {
                // Display logo if available
                backendData.logo_url ? (
                  <img
                    src={backendData.logo_url}
                    className="h-6 w-6 p-1 mr-2 inline-block align-middle rounded-lg object-contain bg-white dark:bg-gray-700"
                    aria-hidden="true"
                  />
                ) : (
                  <UserGroupIcon
                    className="h-6 w-6 mr-2 inline text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                  />
                )
              }
              <span className="text-gray-600 dark:text-gray-300">
                {!backendData.customer_name
                  ? "No customer"
                  : backendData.customer_name}
              </span>
            </div>
            <div className="inline-block mr-12">
              <DevicePhoneMobileIcon
                className="h-6 w-6 mr-2 inline text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <span className="text-gray-600 dark:text-gray-300">
                {backendData.contacts.length === 1
                  ? "1 contact"
                  : backendData.contacts.length > 1
                    ? backendData.contacts.length + " contacts"
                    : "No contacts"}
              </span>
            </div>
          </div>
        </div>

        <div className="px-2 sm:px-6 lg:px-6">
          <UnderlineTabs
            tabs={secondaryTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            actions={
              <>
                {activeTab === "Checkpoints" && (
                  <Button
                    icon={PlusIcon}
                    onClick={() => {
                      setIsCheckpointsAddMode(true);
                      toggleCheckpointsOpen();
                    }}
                    disabled={
                      allowedCheckpoints > 0 &&
                      allowedCheckpoints <= checkpointCount
                    }
                  >
                    {`Create checkpoint${
                      allowedCheckpoints > 0
                        ? ` (${checkpointCount}/${allowedCheckpoints})`
                        : ""
                    }`}
                  </Button>
                )}
                {activeTab === "Tours" && (
                  <Button
                    icon={PlusIcon}
                    onClick={() => {
                      setIsToursAddMode(true);
                      setIsCheckpointsAddMode(true);
                      toggleToursOpen();
                    }}
                  >
                    Create tour
                  </Button>
                )}
                {activeTab === "Posts" && (
                  <Button icon={PlusIcon} onClick={() => setEditingPost({})}>
                    Create post
                  </Button>
                )}
                {activeTab === "Files" && (
                  <Button
                    icon={PlusIcon}
                    onClick={() => filesRef.current?.openPicker()}
                  >
                    Add file
                  </Button>
                )}
              </>
            }
          />
          {/* <Checkpoints
            checkpoints={checkpoints}
            setCheckpoints={setCheckpoints}
          /> */}
          <SecondaryTabs tabs={secondaryTabs} activeTab={activeTab} />
        </div>
      </main>
      <CheckpointDetails
        isOpen={isCheckpointsOpen}
        toggleOpen={toggleCheckpointsOpen}
        selectedCheckpointDetails={selectedCheckpointDetails}
        isAddMode={isCheckpointsAddMode}
        selectedTourId={selectedTourId}
        selectedCheckpointId={selectedCheckpointId}
        setSelectedTourCheckpoints={handleSelectedTourCheckpoints}
        // selectedTourCheckpoints={selectedTourCheckpoints}
        setCheckpoints={setCheckpointsListData}
        fetchCheckpoints={fetchCheckpointsList}
        checkpoints={selectedTourCheckpoints}
        fetchToursList={fetchToursList}
        fetchSelectedCheckpointDetails={fetchSelectedCheckpointDetails}
        selectedCheckpointType={selectedCheckpointType}
      />

      <DeleteConfirm
        isOpen={isCheckpointsDeleteOpen}
        toggleOpen={() =>
          toggleDeleteCheckpointState(
            setIsCheckpointsDeleteOpen,
            isCheckpointsDeleteOpen,
          )
        }
        selectedDeleteItem={selectedCheckpointDeleteItem}
        handleDeleteSuccess={handleDeleteCheckpointSuccess}
        onDelete={deleteCheckpoint}
      />

      <DeleteConfirm
        isOpen={isToursDeleteOpen}
        toggleOpen={() =>
          toggleDeleteTourState(setIsToursDeleteOpen, isToursDeleteOpen)
        }
        selectedDeleteItem={selectedTourDeleteItem}
        handleDeleteSuccess={handleDeleteTourSuccess}
        onDelete={deleteTour}
      />

      <TourDetails
        isOpen={isToursOpen}
        toggleOpen={toggleToursOpen}
        setTours={setTours}
        // tours={tours}
        isAddMode={isToursAddMode}
        selectedTourId={selectedTourId}
        toggleCheckpointsOpen={toggleCheckpointsOpen}
        setSelectedTourCheckpoints={handleSelectedTourCheckpoints}
        selectedTourCheckpoints={selectedTourCheckpoints}
        selectedTourDetails={selectedTourDetails}
        fetchSelectedTourDetails={fetchSelectedTourDetails}
        fetchToursList={fetchToursList}
      />

      <SiteDetailsMeta
        isOpen={isDetailsMetaOpen}
        toggleOpen={toggleDetailsMetaOpen}
        siteData={backendData}
        updateSiteData={fetchSiteDetails}
        onSuccess={handleSuccess}
      />
    </div>
  );
}

export { SiteSettingsDetails };
